import { useState } from "react";
import { api, apiErr, API } from "@/api";
import Overlay from "@/components/Overlay";
import ProductFields from "@/components/ProductFields";
import PhotoIntake from "@/components/PhotoIntake";
import { imagesOf } from "@/components/ProductImages";
import { useTaxonomy } from "@/hooks/useTaxonomy";
import { toast } from "sonner";
import { ImagePlus, X, Star } from "lucide-react";

export default function ProductEditor({ product, credits, setCredits, costs, onClose, onSaved }) {
  const tax = useTaxonomy();
  const [form, setForm] = useState({ ...product, materials: Array.isArray(product.materials) ? product.materials : [], style_tags: product.style_tags || [] });
  const [images, setImages] = useState(imagesOf(product));
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Product name is required"); return; }
    if (!form.category) { toast.error("Choose a primary category"); return; }
    setBusy(true);
    try {
      const body = { name: form.name, category: form.category, materials: form.materials, style_tags: form.style_tags, highlight: form.highlight || "", description: form.description || "", images };
      const { data } = await api.put(`/exhibitor/products/${product.id}`, body);
      toast.success("Product updated");
      onSaved(data);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const remove = (i) => setImages((s) => s.filter((_, j) => j !== i));
  const cover = (i) => setImages((s) => [s[i], ...s.filter((_, j) => j !== i)]);

  return (
    <Overlay onClose={() => !busy && onClose()} testid="product-form-modal">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl gold-text">Edit Product</h3>
        <button onClick={() => !busy && onClose()} className="text-slate-400" data-testid="product-form-close"><X size={20} /></button>
      </div>
      <div className="text-xs text-slate-400 mt-4 mb-2">Photos · first one is the cover</div>
      <div className="grid grid-cols-4 gap-2">
        {images.map((src, i) => (
          <div key={src} className="relative aspect-square rounded-xl overflow-hidden border border-amber-500/20" data-testid={`product-form-image-${i}`}>
            <img src={`${API}/files/${src}`} alt="" className="h-full w-full object-cover" />
            <button onClick={() => remove(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 text-red-500 flex items-center justify-center" data-testid={`product-form-image-remove-${i}`}><X size={12} /></button>
            {i === 0 ? <span className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded-full btn-gold">Cover</span>
              : <button onClick={() => cover(i)} className="absolute bottom-1 left-1 h-6 w-6 rounded-full bg-white/90 text-amber-600 flex items-center justify-center" title="Make cover" data-testid={`product-form-image-cover-${i}`}><Star size={11} /></button>}
          </div>
        ))}
        <button onClick={() => setAdding(true)} className="aspect-square rounded-xl border border-dashed border-amber-500/40 flex flex-col items-center justify-center gap-1 text-[10px] text-slate-400" data-testid="product-add-photo">
          <ImagePlus size={18} className="text-amber-400" /> Add photo
        </button>
      </div>
      <div className="mt-4"><ProductFields value={form} onChange={setForm} tax={tax} /></div>
      <button disabled={busy} onClick={save} className="w-full btn-gold py-3 rounded-xl mt-4 disabled:opacity-60" data-testid="product-save">{busy ? "Saving…" : "Save Product"}</button>
      {adding && (
        <PhotoIntake attachTo={product.id} credits={credits} setCredits={setCredits} costs={costs}
          onApproved={(p) => setImages(imagesOf(p))} onClose={() => setAdding(false)} />
      )}
    </Overlay>
  );
}
